/* ============================================================================
   Practice test page — ALL TEXTS, in four languages.

   Edit wording here; the page (PracticePage.js) and the quiz (SampleQuiz.js)
   only read from this file. Placeholders like {n} are filled in by the code.

   Facts about the real test come from Trafikverket (checked 2026-10-25):
   https://www.trafikverket.se/korkort/ta-korkort/personbil-och-latt-lastbil/
   70 questions, 5 of them unscored test questions (max 65 points),
   52 points to pass, 50 minutes. Update the texts if Trafikverket changes this.
   ========================================================================== */

export const TRAFIKVERKET_URL =
  "https://www.trafikverket.se/korkort/ta-korkort/personbil-och-latt-lastbil/";

/* URL of the practice page in each language: /sv/ovningsprov/, /en/practice-test/ … */
export const PRACTICE_SLUGS = {
  sv: "ovningsprov",
  en: "practice-test",
  uk: "probnyi-test",
  ru: "probnyj-test",
};
export const practicePath = (lang) => `/${lang}/${PRACTICE_SLUGS[lang]}/`;

/* Share of correct answers needed on the real test: 52 of 65 ≈ 80 %. */
export const PASS_RATIO = 52 / 65;

/* Question categories as stored in the backend → label per language. */
export const CATEGORY_LABELS = {
  "Traffic rules":       { sv: "Trafikregler", en: "Traffic rules", uk: "Правила дорожнього руху", ru: "Правила дорожного движения" },
  "Traffic safety":      { sv: "Trafiksäkerhet", en: "Traffic safety", uk: "Безпека дорожнього руху", ru: "Безопасность движения" },
  "Vehicle knowledge":   { sv: "Fordonskännedom", en: "Vehicle knowledge", uk: "Будова автомобіля", ru: "Устройство автомобиля" },
  "Environment":         { sv: "Miljö", en: "Environment", uk: "Довкілля", ru: "Окружающая среда" },
  "Personal conditions": { sv: "Personliga förutsättningar", en: "Personal conditions", uk: "Особисті чинники", ru: "Личные факторы" },
};

export const PRACTICE = {
  /* ------------------------------------------------------------------ SV */
  sv: {
    navLabel: "Övningsprov",
    heroLink: "Testa 10 gratis provfrågor →",
    seo: {
      title: "Övningsprov teori B – 10 gratis körkortsfrågor | Teorikörkort",
      description: "Gör ett gratis övningsprov med 10 körkortsfrågor för B-körkort. Svara direkt i mobilen, se rätt svar med förklaring och få ditt resultat.",
    },
    breadcrumbHome: "Start",
    h1: "Gratis övningsprov för teoriprovet (B)",
    intro: "Testa dina kunskaper med 10 frågor i samma stil som Trafikverkets kunskapsprov. Du ser direkt om du svarade rätt och varför – utan konto och utan att ladda ner något.",

    quiz: {
      startTitle: "10 gratis provfrågor",
      startText: "Svara i din egen takt. Efter varje fråga ser du rätt svar och en förklaring.",
      start: "Starta övningsprovet",
      loading: "Hämtar frågor…",
      error: "Frågorna kunde inte laddas. Kontrollera anslutningen och försök igen.",
      tooMany: "Du har gjort många försök på kort tid. Vänta en stund och försök igen.",
      retry: "Försök igen",
      empty: "Övningsprovet är inte tillgängligt just nu. Du kan öva gratis i appen istället.",
      questionOf: "Fråga {n} av {total}",
      correct: "Rätt svar!",
      wrong: "Fel svar.",
      rightAnswer: "Rätt svar: {answer}",
      why: "Förklaring",
      next: "Nästa fråga",
      finish: "Se resultat",
      resultTitle: "Ditt resultat",
      resultScore: "{score} av {total} rätt",
      resultPass: "Mycket bra! Det motsvarar godkänt-nivån på riktiga provet, där du behöver 52 av 65 poäng (cirka 80 %).",
      resultFail: "Fortsätt öva! På riktiga provet behöver du 52 av 65 poäng (cirka 80 %) för att bli godkänd.",
      restart: "Gör om provet",
      ctaTitle: "Vill du öva på fler frågor?",
      ctaText: "I appen finns över 500 provliknande frågor, alla vägmärken och övningsprov som följer kunskapsprovets upplägg.",
      appStoreAlt: "Ladda ned i App Store",
      googlePlayAlt: "Ladda ned på Google Play",
    },

    howTitle: "Så går kunskapsprovet till",
    how: [
      "Kunskapsprovet för B-körkort görs på dator hos Trafikverket. Provet har 70 frågor med svarsalternativ där ett alternativ är rätt. Fem av frågorna är testfrågor som inte räknas, så du kan som mest få 65 poäng.",
      "För att bli godkänd behöver du minst 52 poäng, och provtiden är 50 minuter. Läs varje fråga noga – små ord som \"alltid\", \"får\" eller \"ska\" kan avgöra svaret.",
    ],
    howSource: "Kontrollera alltid aktuell information hos Trafikverket",

    topicsTitle: "Vad frågorna handlar om",
    topics: [
      ["Trafikregler", "väjningsplikt, hastigheter, vägmärken och regler i korsningar."],
      ["Trafiksäkerhet", "riskmedvetenhet, avstånd, väglag och samspel med andra trafikanter."],
      ["Fordonskännedom", "bilens utrustning, däck, bromsar och kontroller före körning."],
      ["Miljö", "sparsam körning och hur bilen påverkar miljön."],
      ["Personliga förutsättningar", "alkohol, droger, trötthet och hur de påverkar körningen."],
    ],

    tipsTitle: "Tips inför provet",
    tips: [
      "Öva lite varje dag i stället för att plugga allt på en gång.",
      "Läs förklaringen även när du svarar rätt – då förstår du regeln, inte bara svaret.",
      "Lär dig vägmärkena ordentligt; de förekommer i många frågor.",
      "Gör hela övningsprov med tidtagning innan provdagen.",
    ],

    relatedTitle: "Läs mer",
    related: {
      app: "Om appen Teorikörkort",
      faq: "Vanliga frågor om appen",
      official: "Körkort för personbil (Trafikverket)",
    },
  },

  /* ------------------------------------------------------------------ EN */
  en: {
    navLabel: "Practice test",
    heroLink: "Try 10 free practice questions →",
    seo: {
      title: "Free Swedish Theory Test Practice – 10 Questions | Teorikörkort",
      description: "Take a free practice test with 10 questions for the Swedish driving theory test (licence B). Answer on your phone and see the right answer with an explanation.",
    },
    breadcrumbHome: "Home",
    h1: "Free practice test for the Swedish theory test (B)",
    intro: "Test yourself with 10 questions in the same style as Trafikverket's theory test. You see right away whether you were right and why – no account and nothing to download.",

    quiz: {
      startTitle: "10 free test questions",
      startText: "Answer at your own pace. After each question you see the right answer and an explanation.",
      start: "Start the practice test",
      loading: "Loading questions…",
      error: "The questions couldn't be loaded. Check your connection and try again.",
      tooMany: "You've made many attempts in a short time. Please wait a while and try again.",
      retry: "Try again",
      empty: "The practice test isn't available right now. You can practise for free in the app instead.",
      questionOf: "Question {n} of {total}",
      correct: "Correct!",
      wrong: "Wrong answer.",
      rightAnswer: "Right answer: {answer}",
      why: "Explanation",
      next: "Next question",
      finish: "See your score",
      resultTitle: "Your score",
      resultScore: "{score} of {total} correct",
      resultPass: "Great job! That matches the pass level of the real test, where you need 52 of 65 points (about 80%).",
      resultFail: "Keep practising! On the real test you need 52 of 65 points (about 80%) to pass.",
      restart: "Try again",
      ctaTitle: "Want to practise more questions?",
      ctaText: "The app has 500+ exam-style questions, every road sign and full practice tests that follow the real test.",
      appStoreAlt: "Download on the App Store",
      googlePlayAlt: "Get it on Google Play",
    },

    howTitle: "How the theory test works",
    how: [
      "The theory test for a category B licence is taken on a computer at Trafikverket. It has 70 multiple-choice questions with one right answer each. Five of them are unscored test questions, so the maximum is 65 points.",
      "You need at least 52 points to pass, and you have 50 minutes. Read every question carefully – small words like \"always\", \"may\" or \"must\" can decide the answer.",
    ],
    howSource: "Always check the latest information from Trafikverket",

    topicsTitle: "What the questions cover",
    topics: [
      ["Traffic rules", "giving way, speed limits, road signs and rules at junctions."],
      ["Traffic safety", "hazard awareness, following distance, road conditions and sharing the road."],
      ["Vehicle knowledge", "the car's equipment, tyres, brakes and checks before driving."],
      ["Environment", "eco-driving and how cars affect the environment."],
      ["Personal conditions", "alcohol, drugs, tiredness and how they affect driving."],
    ],

    tipsTitle: "Tips for the test",
    tips: [
      "Practise a little every day instead of cramming.",
      "Read the explanation even when you're right – you learn the rule, not just the answer.",
      "Learn the road signs well; they appear in many questions.",
      "Take full timed practice tests before test day.",
    ],

    relatedTitle: "Read more",
    related: {
      app: "About the Teorikörkort app",
      faq: "Frequently asked questions",
      official: "Driving licence for cars (Trafikverket, in Swedish)",
    },
  },

  /* ------------------------------------------------------------------ UK */
  uk: {
    navLabel: "Пробний тест",
    heroLink: "Спробуй 10 безкоштовних питань →",
    seo: {
      title: "Безкоштовний пробний тест з теорії водіння у Швеції | Teorikörkort",
      description: "Пройди безкоштовний пробний тест із 10 питань до теоретичного іспиту на права категорії B у Швеції. Одразу бачиш правильну відповідь із поясненням.",
    },
    breadcrumbHome: "Головна",
    h1: "Безкоштовний пробний тест до теоретичного іспиту (B)",
    intro: "Перевір себе на 10 питаннях у стилі теоретичного іспиту Trafikverket. Ти одразу бачиш, чи правильна відповідь і чому – без акаунта й без завантажень.",

    quiz: {
      startTitle: "10 безкоштовних питань",
      startText: "Відповідай у своєму темпі. Після кожного питання ти бачиш правильну відповідь і пояснення.",
      start: "Почати пробний тест",
      loading: "Завантажуємо питання…",
      error: "Не вдалося завантажити питання. Перевір з'єднання й спробуй ще раз.",
      tooMany: "Забагато спроб за короткий час. Зачекай трохи й спробуй ще раз.",
      retry: "Спробувати ще раз",
      empty: "Пробний тест зараз недоступний. Ти можеш безкоштовно потренуватися в застосунку.",
      questionOf: "Питання {n} з {total}",
      correct: "Правильно!",
      wrong: "Неправильно.",
      rightAnswer: "Правильна відповідь: {answer}",
      why: "Пояснення",
      next: "Наступне питання",
      finish: "Переглянути результат",
      resultTitle: "Твій результат",
      resultScore: "{score} з {total} правильно",
      resultPass: "Чудово! Це відповідає прохідному рівню справжнього іспиту, де потрібно 52 з 65 балів (близько 80 %).",
      resultFail: "Тренуйся далі! На справжньому іспиті потрібно 52 з 65 балів (близько 80 %), щоб скласти.",
      restart: "Пройти ще раз",
      ctaTitle: "Хочеш більше питань?",
      ctaText: "У застосунку понад 500 питань як на іспиті, усі дорожні знаки та повні пробні іспити у форматі справжнього.",
      appStoreAlt: "Завантажити в App Store",
      googlePlayAlt: "Завантажити в Google Play",
    },

    howTitle: "Як проходить теоретичний іспит",
    how: [
      "Теоретичний іспит на права категорії B складають на комп'ютері в Trafikverket. Він має 70 питань із варіантами відповідей, де правильна лише одна. П'ять питань – тестові й не зараховуються, тож максимум – 65 балів.",
      "Щоб скласти, потрібно щонайменше 52 бали, а на іспит дається 50 хвилин. Уважно читай кожне питання – дрібні слова на кшталт «завжди», «можна» чи «повинен» можуть вирішити відповідь.",
    ],
    howSource: "Актуальну інформацію завжди перевіряй на сайті Trafikverket",

    topicsTitle: "Про що питання",
    topics: [
      ["Правила дорожнього руху", "пріоритет, обмеження швидкості, дорожні знаки та проїзд перехресть."],
      ["Безпека дорожнього руху", "оцінка ризиків, дистанція, стан дороги та взаємодія з іншими учасниками руху."],
      ["Будова автомобіля", "обладнання авто, шини, гальма та перевірки перед поїздкою."],
      ["Довкілля", "економне водіння та вплив автомобіля на довкілля."],
      ["Особисті чинники", "алкоголь, наркотики, втома та їхній вплив на керування."],
    ],

    tipsTitle: "Поради перед іспитом",
    tips: [
      "Тренуйся потроху щодня, а не все за один раз.",
      "Читай пояснення навіть після правильної відповіді – так ти розумієш правило, а не лише відповідь.",
      "Добре вивчи дорожні знаки: вони трапляються в багатьох питаннях.",
      "Перед іспитом пройди кілька повних пробних тестів на час.",
    ],

    relatedTitle: "Читати далі",
    related: {
      app: "Про застосунок Teorikörkort",
      faq: "Поширені запитання",
      official: "Права на легковий автомобіль (Trafikverket, шведською)",
    },
  },

  /* ------------------------------------------------------------------ RU */
  ru: {
    navLabel: "Пробный тест",
    heroLink: "Попробуй 10 бесплатных вопросов →",
    seo: {
      title: "Бесплатный пробный тест по теории вождения в Швеции | Teorikörkort",
      description: "Пройди бесплатный пробный тест из 10 вопросов к теоретическому экзамену на права категории B в Швеции. Сразу видишь правильный ответ с объяснением.",
    },
    breadcrumbHome: "Главная",
    h1: "Бесплатный пробный тест к теоретическому экзамену (B)",
    intro: "Проверь себя на 10 вопросах в стиле теоретического экзамена Trafikverket. Ты сразу видишь, правильный ли ответ и почему – без аккаунта и без загрузок.",

    quiz: {
      startTitle: "10 бесплатных вопросов",
      startText: "Отвечай в своём темпе. После каждого вопроса ты видишь правильный ответ и объяснение.",
      start: "Начать пробный тест",
      loading: "Загружаем вопросы…",
      error: "Не удалось загрузить вопросы. Проверь соединение и попробуй ещё раз.",
      tooMany: "Слишком много попыток за короткое время. Подожди немного и попробуй снова.",
      retry: "Попробовать снова",
      empty: "Пробный тест сейчас недоступен. Ты можешь бесплатно потренироваться в приложении.",
      questionOf: "Вопрос {n} из {total}",
      correct: "Правильно!",
      wrong: "Неправильно.",
      rightAnswer: "Правильный ответ: {answer}",
      why: "Объяснение",
      next: "Следующий вопрос",
      finish: "Посмотреть результат",
      resultTitle: "Твой результат",
      resultScore: "{score} из {total} правильно",
      resultPass: "Отлично! Это соответствует проходному уровню настоящего экзамена, где нужно 52 из 65 баллов (около 80 %).",
      resultFail: "Продолжай тренироваться! На настоящем экзамене нужно 52 из 65 баллов (около 80 %), чтобы сдать.",
      restart: "Пройти ещё раз",
      ctaTitle: "Хочешь больше вопросов?",
      ctaText: "В приложении более 500 вопросов как на экзамене, все дорожные знаки и полные пробные экзамены в формате настоящего.",
      appStoreAlt: "Загрузить в App Store",
      googlePlayAlt: "Доступно в Google Play",
    },

    howTitle: "Как проходит теоретический экзамен",
    how: [
      "Теоретический экзамен на права категории B сдают на компьютере в Trafikverket. В нём 70 вопросов с вариантами ответов, где правильный только один. Пять вопросов – тестовые и не засчитываются, поэтому максимум – 65 баллов.",
      "Чтобы сдать, нужно не меньше 52 баллов, а на экзамен даётся 50 минут. Внимательно читай каждый вопрос – маленькие слова вроде «всегда», «можно» или «обязан» могут решить ответ.",
    ],
    howSource: "Актуальную информацию всегда проверяй на сайте Trafikverket",

    topicsTitle: "О чём вопросы",
    topics: [
      ["Правила дорожного движения", "приоритет, ограничения скорости, дорожные знаки и проезд перекрёстков."],
      ["Безопасность движения", "оценка рисков, дистанция, состояние дороги и взаимодействие с другими участниками."],
      ["Устройство автомобиля", "оборудование машины, шины, тормоза и проверки перед поездкой."],
      ["Окружающая среда", "экономичное вождение и влияние автомобиля на природу."],
      ["Личные факторы", "алкоголь, наркотики, усталость и их влияние на вождение."],
    ],

    tipsTitle: "Советы перед экзаменом",
    tips: [
      "Тренируйся понемногу каждый день, а не всё за один раз.",
      "Читай объяснение даже после правильного ответа – так ты понимаешь правило, а не только ответ.",
      "Хорошо выучи дорожные знаки: они встречаются во многих вопросах.",
      "Перед экзаменом пройди несколько полных пробных тестов на время.",
    ],

    relatedTitle: "Читать дальше",
    related: {
      app: "О приложении Teorikörkort",
      faq: "Частые вопросы",
      official: "Права на легковой автомобиль (Trafikverket, на шведском)",
    },
  },
};

/* Fill {placeholders}: fill("Fråga {n} av {total}", { n: 1, total: 10 }) */
export function fill(template, values) {
  return template.replace(/\{(\w+)\}/g, (match, key) => (key in values ? String(values[key]) : match));
}
